import { Entity, registerComponent } from "aframe";
import { AvatarActionFactory, AvatarSystem } from "../avatar";
import { AvatarInteractionSystem } from "../interaction/interaction";
import { OrientationSystem } from "../orientation";
import { GetRayCallback, InputActionDetail, InputActionEvent, InputComponent, InputSystem, press, release } from "./common";

interface OnScreenInputComponentData
{
	device : HTMLElement;
	enabled : boolean;
	action : string;
	label : string;
	icon : string;
}

interface OnScreenInputComponent extends InputComponent
{
	data : OnScreenInputComponentData;
	updateDeviceState() : void;
	feedback(intensity : number, ms? : number, action? : string) : void;
}

const withCapture = { capture : true };
const onceWithCapture = { once : true, capture : true };
const clampToOne = (v : number) => ((v > 1) ? 1 : ((v < -1) ? -1 : v));

registerComponent("on-screen-input", {
	
	schema : {
		enabled : { type : "boolean", default : true },
		device : { type : "selector" },
		action : { type : "string" },
		label : { type : "string" },
		icon : { type : "string" }
	},

	multiple : true,

	updateDeviceState(this : OnScreenInputComponent)
	{
		const device = this.data.device;
		if(!device)
			return; //Not initialized yet.

		const scene = this.el.sceneEl!;
		const avatarSystem = scene.systems["avatar"] as AvatarSystem;

		const enabled = this.data.enabled && !( scene.is("ui-mode") || scene.is("vr-mode") );
		const mainAction = avatarSystem.actions[ this.data.action ];

		device.classList.toggle("with-icon", Boolean(this.data.icon));
		device.style.setProperty("--icon", this.data.icon ? ("url(" + this.data.icon + ")") : null);

		device.classList.toggle("with-label", Boolean(this.data.label));
		device.dataset.label = this.data.label;
		device.title = this.data.label;
		 		
		device.classList.toggle("enabled", enabled);

		let atLeastOneAction : boolean = false;
		for( let button of Array.from(device.querySelectorAll<HTMLElement>(".input.button[data-action]")) )
		{
			const inputAction : AvatarActionFactory | undefined = avatarSystem.actions[ button.dataset.action! ];
				
			if("disabled" in button)
				(button as any).disabled = !inputAction;
			button.classList.toggle("active", Boolean(inputAction));
			button.classList.toggle("main", inputAction === mainAction);

			let label : string = "", inputKey : string = "", inputName : string = "", icon : string = "", keyUncertain = false;
			if(inputAction)
			{
				if(inputAction.label)
					label = inputAction.label;
				if(inputAction.icon)
					icon = inputAction.icon;

				for(let sysName in scene.systems)
				{
					const inputSys = scene.systems[sysName] as InputSystem;
					if(!(inputSys && inputSys.isPrimaryInput))
					 	continue;

					for(let key in inputSys.data)
					{
						const keyAction = inputSys.data[key]; //What action is bound to this input key ?
						if( (inputAction === mainAction) && (keyAction === this.data.action) )
						{
							inputName = sysName;
							inputKey = key;
							break; //This is the main action : prefer this.
						}
						else if(keyAction == button.dataset.action)
						{
							inputName = sysName;
							inputKey = key;
						}
						else
							continue;
					}

					if(inputName)
					{
						if( inputSys.layout && !/^([0-9]|Esc)$/.test(inputKey)  ) //Input device with layout data + not a digit or Escape
						{
							const layoutKey = inputSys.layout[inputKey];
							if(layoutKey)
								inputKey = layoutKey;
							else
								keyUncertain = true;
						}
						break;
					}
				}

				atLeastOneAction = true;
			}

			button.innerHTML = "";
			button.dataset.input = inputName;

			if(label)
			{
				const lbl = document.createElement("label");
				lbl.append(label);
				button.append(lbl);
				button.title = inputKey ? (label + " (" + inputKey +")") : label;
			}
			
			if(inputKey)
			{
				const span = document.createElement("span");
				span.classList.add("input-key");
				span.classList.toggle("uncertain", keyUncertain);
				span.append(inputKey);
				
				button.append(span);
				button.dataset.inputKey = inputKey;
			}

			button.classList.toggle("with-label", Boolean(label));
			button.classList.toggle("with-icon", Boolean(icon));
			button.style.setProperty("--icon", icon ? ("url(" + icon + ")") : "");
		}

		atLeastOneAction ||= Boolean( device.querySelector(".input.stick") );

		device.classList.toggle("active", atLeastOneAction);
	},

	feedback(this : OnScreenInputComponent, intensity : number, milliseconds : number, action : string)
	{
		const target = action ? this.data.device.querySelector<HTMLElement>('.input[data-action="' + action + '"]') : this.data.device;
		if(!target)
			return;

		target.style.setProperty("--feedback-intensity", String(intensity));
		target.classList.add("feedback");
		setTimeout( () =>  {
			target.classList.remove("feedback");
			target.style.removeProperty("--feedback-intensity");
		}, isNaN(milliseconds) ? 500 : milliseconds );
	},

	init(this : OnScreenInputComponent)
	{
		this.actions = {};

		const scene = this.el.sceneEl!;
		const updateListener = () => this.updateDeviceState();
		this.el.addEventListener("actions-changed", updateListener);
		scene.addEventListener("primary-input-changed", updateListener);
		scene.addEventListener("enter-vr", updateListener);
		scene.addEventListener("exit-vr", updateListener);
		scene.addEventListener("enter-ui", updateListener);
		scene.addEventListener("exit-ui", updateListener);
	},

	update(this : OnScreenInputComponent, oldData : OnScreenInputComponentData)
	{
		if ( oldData && (oldData.device != this.data.device) )
		{
			const scene = this.el.sceneEl!;
			const oriSystem = scene.systems.orientation as OrientationSystem;

			const getRay : GetRayCallback = (o,d,s) =>
			{
				const camMat = scene.camera.matrixWorld.elements;
				s.call(o, camMat[12], camMat[13], camMat[14]);
				s.call(d, -camMat[8], -camMat[9], -camMat[10]);
			};


			const device = this.data.device;
			const feedback = (i : number, ms? : number, ac? : string) => this.feedback(i, ms, ac);

			
			oldData.device?.dispatchEvent(new CustomEvent("reset-device"));
			device.dispatchEvent(new CustomEvent("reset-device"));


			const pointerDown = ((event : PointerEvent) =>
			{
				scene.addEventListener("pointerup", pointerUp);
				event.stopPropagation();
			}) as EventListener;
			const pointerUp = ((event : PointerEvent) =>
			{
				const targetButton = (event.target as Entity).closest('.input.button[data-action]');
				const targetAction = targetButton && targetButton.dataset.action;
				if(targetAction) {
					const detail : InputActionDetail = {
						input : this,
						timestamp : Date.now(),
						action : targetAction,
						amount : 1,
						getRay, 
						feedback,
						instant : true
					};
					this.el.dispatchEvent(new CustomEvent("input-begin", {detail, bubbles : true, cancelable : true}));
				}



				scene.removeEventListener("pointerup", pointerUp);
				event.stopPropagation();
			}) as EventListener;
			
			
			const deviceReset = (event : Event) =>
			{
				device.removeEventListener("pointerdown", pointerDown);
				scene.removeEventListener("pointerup", pointerUp);
				device.removeEventListener("reset-device", deviceReset);
			};
			device.addEventListener("pointerdown", pointerDown);
			device.addEventListener("reset-device", deviceReset);

			



			Array.from(device.querySelectorAll<HTMLElement>(".input")).forEach( (input) =>
			{
				
				
				if( input.matches(".button") )
				{
					const action = input.dataset.action!;
					
					const pointerDown = (event: PointerEvent) =>
					{
						input.setPointerCapture(event.pointerId);

						const button = (event as MouseEvent).button;
						press(this, action, String(button), 1, getRay, feedback);

						const finish = () =>
						{
							input.releasePointerCapture(event.pointerId);

							release(this, action, String(button));
							window.removeEventListener("pointerup", pointerUp as EventListener, withCapture );
							window.removeEventListener("blur", finish);
						};
						const pointerUp = (endEvent : PointerEvent) =>
						{
							if(endEvent.button != button) return;
							if(endEvent.pointerId != event.pointerId) return;
							finish();
						};

						window.addEventListener("pointerup", pointerUp, withCapture );
						window.addEventListener("blur", finish);

						event.preventDefault();
						event.stopPropagation();
					};

					const actionInputBegin = ((beginEvent : InputActionEvent) =>
					{
						if(!input.matches(":hover")) return; //Not pointed
						const inputAction = beginEvent.detail.action;
						if(! ["Action", "RightAction", "LeftAction"].includes(inputAction)) return;

						let emitter : Entity | undefined = undefined;
						if(inputAction.startsWith("Right") || inputAction.startsWith("Left"))
						{
							const interactionSystem = scene.systems["avatar-interaction"] as AvatarInteractionSystem;
							const interactionTarget = inputAction.startsWith("Left") ? interactionSystem.leftHand : interactionSystem.rightHand;
							emitter = interactionTarget.slot || undefined;
						}

						const detail : InputActionDetail = {
							input : this,
							action,
							amount : 1,
							feedback, getRay, timestamp : Date.now(),
							instant : beginEvent.detail.instant
						};
						(emitter || this.el).dispatchEvent(new CustomEvent("input-begin", { detail, bubbles : true, cancelable : true } ));
						//press(this, action, inputAction, 1, getRay, feedback, emitter);

						beginEvent.preventDefault();
						beginEvent.stopPropagation();

						if( beginEvent.detail.instant )
							return;
						
						const finish = () => {

							detail.amount = 0;
							(emitter || this.el).dispatchEvent(new CustomEvent("input-end", { detail, bubbles : true, cancelable : true } ));

							input.removeEventListener("pointerleave", finish);
							window.removeEventListener("blur", finish);
							scene.removeEventListener("input-end", inputEnd, withCapture );
						};
						const inputEnd = ((endEvent : InputActionEvent) =>
						{
							if(endEvent.detail !== beginEvent.detail) return;
							finish();
							endEvent.preventDefault();
							endEvent.stopPropagation();
						}) as EventListener;

						window.addEventListener("blur", finish);
						input.addEventListener("pointerleave", finish);
						scene.addEventListener("input-end", inputEnd, withCapture );
					}) as EventListener;

					const resetdevice = (event : Event) =>
					{
						input.removeEventListener("pointerdown", pointerDown, withCapture);
						scene.removeEventListener("input-begin", actionInputBegin, withCapture);
						event.target!.removeEventListener("reset-device", resetdevice);
					};
					scene.addEventListener("input-begin", actionInputBegin, withCapture);
					input.addEventListener("pointerdown", pointerDown, withCapture);
					device.addEventListener("reset-device", resetdevice);
				}

				if( input.matches(".stick") )
				{
					const state = {
						x0 : NaN, y0 : NaN, //Start position
						x : 0, y : 0, //Current value
						kX : 0, kY : 0
					};

					const hAction = (v : number) => (v > 0) ? input.dataset.actionRight : input.dataset.actionLeft;
					const vAction = (v : number) => (v > 0) ? input.dataset.actionUp : input.dataset.actionDown;

					const pointerDown = (event : PointerEvent) =>
					{
						const bounds = input.getBoundingClientRect();
						state.kX = (input.dataset.actionRight && input.dataset.actionLeft)  ? (2.0 / bounds.width) : 0;
						state.kY = (input.dataset.actionUp && input.dataset.actionDown) ? (2.0 / bounds.height) : 0;
						const pos = oriSystem.convertCoordinates(event);
						state.x0 = pos.x;
						state.y0 = pos.y;
						input.addEventListener("pointermove", pointerMove, withCapture);
						input.addEventListener("pointerup", pointerUp, onceWithCapture);
						input.setPointerCapture(event.pointerId);
						event.preventDefault();
						event.stopPropagation();
					};

					const pointerUp = (event : PointerEvent) => 
					{
						input.releasePointerCapture(event.pointerId);
						input.removeEventListener("pointermove", pointerMove, withCapture);
						if( state.x != 0 )
						{
							release(this, hAction(state.x) || "", "X");
							input.style.setProperty("--x", "0");
						}
						if( state.y != 0 )
						{
							release(this, vAction(state.y) || "", "Y");
							input.style.setProperty("--y", "0");
						}
						event.preventDefault();
						event.stopPropagation();
					};

					const pointerMove = (event : PointerEvent) =>
					{
						const pos = oriSystem.convertCoordinates(event as MouseEvent);
						let x = pos.x, y = pos.y;
						x = clampToOne( (x - state.x0) * state.kX );
						y = clampToOne( (state.y0 - y) * state.kY );
						if( input.dataset.round )
						{
							const r = Math.sqrt( x*x + y*y ), k = parseFloat( input.dataset.round );
							if(r > 1)
							{
								x += k * ((x / r) - x);
								y += k * ((y / r) - y);
							}
						}

						if( state.kX )
						{
							if( (state.x != 0) && ((x * state.x) <= 0) )
								release(this, hAction(state.x) || "", "X");
							if( (state.x = x) != 0 )
								press(this, hAction(x) || "", "X", Math.abs(x), getRay, feedback );
								input.style.setProperty("--x", String(x));
						}
						
						if( state.kY )
						{
							if( (state.y != 0) && ((y * state.y) <= 0) )
								release(this, vAction(state.y) || "", "Y");
							if( (state.y = y) != 0 )
								press(this, vAction(y) || "", "Y", Math.abs(y), getRay, feedback );
								input.style.setProperty("--y", String(y));
						}
						
						event.preventDefault();
						event.stopPropagation();
					};

					const resetdevice = (event : Event) =>
					{
						input.removeEventListener("pointerdown", pointerDown, withCapture);
						input.removeEventListener("pointermove", pointerMove, withCapture);
						input.removeEventListener("pointerup", pointerUp, withCapture);
						event.target!.removeEventListener("reset-device", resetdevice);
					};

					input.addEventListener("pointerdown", pointerDown, withCapture);
					device.addEventListener("reset-device", resetdevice);
					
					if(input.dataset.actionRight && input.dataset.actionLeft)
						input.style.setProperty("--x", "0");
					if(input.dataset.actionUp && input.dataset.actionDown)
						input.style.setProperty("--y", "0");

				}
			});
		}
		
		this.updateDeviceState();
	}
});
